import React, { useState, useRef, useEffect } from 'react';
import FileUpload, { UploadedFile } from './FileUpload';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

interface ChatInputProps {
  inputMessage: string;
  isLoading: boolean;
  uploadedFiles: UploadedFile[];
  hasEverHadFile?: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSendMessage: (e: React.FormEvent) => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDeleteFile: (index: number) => void;
  isWebSearchEnabled?: boolean;
  isImageGenerationEnabled?: boolean;
  isDeepResearchEnabled?: boolean;
  onWebSearchToggle?: (enabled: boolean) => void;
  onImageGenerationToggle?: (enabled: boolean) => void;
  onDeepResearchToggle?: (enabled: boolean) => void;
  isChatStarted?: boolean;
  compact?: boolean;
  isAgentMode?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  inputMessage,
  isLoading,
  uploadedFiles,
  hasEverHadFile = false,
  onInputChange,
  onSendMessage,
  onFileUpload,
  onDeleteFile,
  isWebSearchEnabled: isWebSearchEnabledProp = false,
  isImageGenerationEnabled: isImageGenerationEnabledProp = false,
  isDeepResearchEnabled: isDeepResearchEnabledProp = false,
  onWebSearchToggle,
  onImageGenerationToggle,
  onDeepResearchToggle,
  isChatStarted = false,
  compact = false,
  isAgentMode = false
}) => {
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const isWebSearchEnabled = isWebSearchEnabledProp;
  const isImageGenerationEnabled = isImageGenerationEnabledProp;
  const isDeepResearchEnabled = isDeepResearchEnabledProp;
  const { colors, theme } = useTheme();
  const { user } = useAuth();

  const hasImageGenerationAccess =
    user?.roles?.includes("TheChatImageGeneration") ||
    user?.roles?.includes("Admin");

  const hasDeepResearchAccess =
    user?.roles?.includes("TheChatDeepResearch") ||
    user?.roles?.includes("Admin");

  const hasFiles = uploadedFiles.length > 0;
  const hasUploadedImages = hasFiles && uploadedFiles[0].type.startsWith('image/');
  const hasUploadedNonImage = hasFiles && !uploadedFiles[0].type.startsWith('image/');

  // Auto-disable web search when file is uploaded
  useEffect(() => {
    if (hasFiles && isWebSearchEnabled) {
      onWebSearchToggle?.(false);
    }
  }, [hasFiles, isWebSearchEnabled, onWebSearchToggle]);

  // Auto-disable image generation when non-image file is uploaded
  useEffect(() => {
    if (hasUploadedNonImage && isImageGenerationEnabled) {
      onImageGenerationToggle?.(false);
    }
  }, [hasUploadedNonImage, isImageGenerationEnabled, onImageGenerationToggle]);

  // Auto-disable deep research when image is uploaded
  useEffect(() => {
    if (hasUploadedImages && isDeepResearchEnabled) {
      onDeepResearchToggle?.(false);
    }
  }, [hasUploadedImages, isDeepResearchEnabled, onDeepResearchToggle]);

  const toolsMenuRef = useRef<HTMLDivElement>(null);
  const toolsButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(event.target as Node) &&
          toolsButtonRef.current && !toolsButtonRef.current.contains(event.target as Node)) {
        setShowToolsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToolsClick = () => {
    setShowToolsMenu(!showToolsMenu);
  };

  return (
    <div style={{
      position: 'relative',
      ...(compact ? { maxWidth: '800px', margin: '0 auto' } : {})
    }}>
      {!isAgentMode && (
        <FileUpload
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onDeleteFile={onDeleteFile}
        />
      )}

      {/* Input bubble container */}
      <div style={{
        border: `1px solid ${colors.border}`,
        borderRadius: '20px',
        backgroundColor: colors.surface,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        padding: '20px 24px 16px 24px'
      }}>
        <form onSubmit={onSendMessage}>
          {/* Text input area */}
          <textarea
            value={inputMessage}
            onChange={onInputChange}
            placeholder="Type your message here..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSendMessage(e);
              }
            }}
            style={{
              width: '100%',
              border: 'none',
              fontSize: '16px',
              outline: 'none',
              minHeight: '60px',
              maxHeight: '200px',
              resize: 'none',
              boxSizing: 'border-box',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              lineHeight: '1.5',
              backgroundColor: 'transparent',
              color: colors.textPrimary,
              marginBottom: '5px'
            }}
            disabled={isLoading}
            autoFocus
          />

          {/* Button row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '40px' // Ensure consistent height
          }}>
            {/* Left side buttons - positioned to account for the + button */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginLeft: isAgentMode ? '0' : '45px', // Leave space for the + button when not in agent mode
              height: '40px' // Match button height
            }}>
              {/* Tools button - hidden in agent mode */}
              {!isAgentMode && (
                <button
                  ref={toolsButtonRef}
                  type="button"
                  onClick={handleToolsClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.surface,
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: colors.textSecondary,
                    transition: 'all 0.2s ease',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    height: '40px' // Match other buttons
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.buttonHover;
                    e.currentTarget.style.borderColor = colors.border;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = colors.surface;
                    e.currentTarget.style.borderColor = colors.border;
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="4" y1="21" x2="4" y2="14"/>
                    <line x1="4" y1="10" x2="4" y2="3"/>
                    <line x1="12" y1="21" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12" y2="3"/>
                    <line x1="20" y1="21" x2="20" y2="16"/>
                    <line x1="20" y1="12" x2="20" y2="3"/>
                    <line x1="1" y1="14" x2="7" y2="14"/>
                    <line x1="9" y1="8" x2="15" y2="8"/>
                    <line x1="17" y1="16" x2="23" y2="16"/>
                  </svg>
                  Tools
                </button>
              )}

              {/* Web Search Indicator */}
              {isWebSearchEnabled && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '16px',
                  backgroundColor: hasFiles ? colors.surfaceElevated : '#dbeafe',
                  border: `1px solid ${hasFiles ? colors.border : '#3b82f6'}`,
                  fontSize: '14px',
                  color: hasFiles ? colors.textMuted : '#1d4ed8',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  cursor: hasFiles ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: hasFiles ? 0.6 : 1
                }}
                onClick={() => {
                  if (!hasFiles) {
                    onWebSearchToggle?.(false);
                  }
                }}
                onMouseEnter={(e) => {
                  if (!hasFiles) {
                    e.currentTarget.style.backgroundColor = '#bfdbfe';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!hasFiles) {
                    e.currentTarget.style.backgroundColor = '#dbeafe';
                  }
                }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/>
                    <path d="M2 12h20"/>
                  </svg>
                  Search
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </div>
              )}

              {/* Image Generation Indicator */}
              {isImageGenerationEnabled && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '16px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #f59e0b',
                  fontSize: '14px',
                  color: '#b45309',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => {
                  onImageGenerationToggle?.(false);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#fde68a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fef3c7';
                }}
                title="Image Generation"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                  Image Generation
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </div>
              )}

              {/* Deep Research Indicator */}
              {isDeepResearchEnabled && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '16px',
                  backgroundColor: '#f3e8ff',
                  border: '1px solid #a855f7',
                  fontSize: '14px',
                  color: '#7e22ce',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => {
                  onDeepResearchToggle?.(false);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e9d5ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3e8ff';
                }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                  </svg>
                  Deep Research
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </div>
              )}
            </div>

            {/* Right side button */}
            <button
              type="submit"
              disabled={isLoading || !inputMessage.trim()}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: isLoading || !inputMessage.trim() ? colors.border : colors.sendButton,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isLoading || !inputMessage.trim() ? 'not-allowed' : 'pointer',
                fontSize: '18px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!isLoading && inputMessage.trim()) {
                  e.currentTarget.style.backgroundColor = colors.sendButtonHover;
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading && inputMessage.trim()) {
                  e.currentTarget.style.backgroundColor = colors.sendButton;
                }
              }}
            >
              {isLoading ? (
                <div style={{
                  display: 'flex',
                  gap: '2px',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {[0, 1, 2].map((index) => (
                    <div
                      key={index}
                      style={{
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        backgroundColor: 'currentColor',
                        animation: `pulse 1.4s ease-in-out ${index * 0.2}s infinite`,
                        opacity: 0.4
                      }}
                    />
                  ))}
                  <style>{`
                    @keyframes pulse {
                      0%, 80%, 100% {
                        opacity: 0.4;
                        transform: scale(1);
                      }
                      40% {
                        opacity: 1;
                        transform: scale(1.2);
                      }
                    }
                  `}</style>
                </div>
              ) : '↑'}
            </button>
          </div>
        </form>
      </div>

      {/* Tools dropdown menu */}
      {showToolsMenu && (
        <div
          ref={toolsMenuRef}
          style={{
            position: 'absolute',
            bottom: '60px',
            left: '0px',
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            padding: '8px',
            minWidth: '200px',
            zIndex: 1000
          }}
        >
          <button
            type="button"
            disabled={!!(hasFiles || isDeepResearchEnabled || isImageGenerationEnabled)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: 'none',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              borderRadius: '8px',
              cursor: (hasFiles || isDeepResearchEnabled || isImageGenerationEnabled) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              color: (hasFiles || isDeepResearchEnabled || isImageGenerationEnabled) ? colors.textMuted : colors.textSecondary,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              transition: 'background-color 0.2s ease',
              opacity: (hasFiles || isDeepResearchEnabled || isImageGenerationEnabled) ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!hasFiles && !isDeepResearchEnabled && !isImageGenerationEnabled) {
                e.currentTarget.style.backgroundColor = colors.surfaceElevated;
              }
            }}
            onMouseLeave={(e) => {
              if (!hasFiles && !isDeepResearchEnabled && !isImageGenerationEnabled) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
            onClick={() => {
              if (!hasFiles && !isDeepResearchEnabled && !isImageGenerationEnabled) {
                const newState = !isWebSearchEnabled;
                setShowToolsMenu(false);
                onWebSearchToggle?.(newState);
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/>
              <path d="M2 12h20"/>
            </svg>
            Search the web
          </button>

          {/* Image Generation button */}
          <button
            type="button"
            disabled={!!(!hasImageGenerationAccess || hasUploadedNonImage || isWebSearchEnabled || isDeepResearchEnabled)}
            title={!hasImageGenerationAccess ? 'You do not have access to this feature' : undefined}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: 'none',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              borderRadius: '8px',
              cursor: (!hasImageGenerationAccess || hasUploadedNonImage || isWebSearchEnabled || isDeepResearchEnabled) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              color: (!hasImageGenerationAccess || hasUploadedNonImage || isWebSearchEnabled || isDeepResearchEnabled) ? colors.textMuted : colors.textSecondary,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              transition: 'background-color 0.2s ease',
              opacity: (!hasImageGenerationAccess || hasUploadedNonImage || isWebSearchEnabled || isDeepResearchEnabled) ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (hasImageGenerationAccess && !hasUploadedNonImage && !isWebSearchEnabled && !isDeepResearchEnabled) {
                e.currentTarget.style.backgroundColor = colors.surfaceElevated;
              }
            }}
            onMouseLeave={(e) => {
              if (hasImageGenerationAccess && !hasUploadedNonImage && !isWebSearchEnabled && !isDeepResearchEnabled) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
            onClick={() => {
              if (hasImageGenerationAccess && !hasUploadedNonImage && !isWebSearchEnabled && !isDeepResearchEnabled) {
                const newState = !isImageGenerationEnabled;
                if (newState) {
                  onWebSearchToggle?.(false);
                }
                setShowToolsMenu(false);
                onImageGenerationToggle?.(newState);
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
            Generate image
          </button>

          {/* Deep Research button - only show when chat hasn't started */}
          {!isChatStarted && (
            <button
              type="button"
              disabled={!!(!hasDeepResearchAccess || hasUploadedImages)}
              title={!hasDeepResearchAccess ? 'You do not have access to this feature' : undefined}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                backgroundColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                borderRadius: '8px',
                cursor: (!hasDeepResearchAccess || hasUploadedImages) ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                color: (!hasDeepResearchAccess || hasUploadedImages) ? colors.textMuted : colors.textSecondary,
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                transition: 'background-color 0.2s ease',
                opacity: (!hasDeepResearchAccess || hasUploadedImages) ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (hasDeepResearchAccess && !hasUploadedImages) {
                  e.currentTarget.style.backgroundColor = colors.surfaceElevated;
                }
              }}
              onMouseLeave={(e) => {
                if (hasDeepResearchAccess && !hasUploadedImages) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
              onClick={() => {
                if (hasDeepResearchAccess && !hasUploadedImages) {
                  const newState = !isDeepResearchEnabled;
                  // Disable web search and image generation when deep research is enabled
                  if (newState) {
                    onWebSearchToggle?.(false);
                    onImageGenerationToggle?.(false);
                  }
                  setShowToolsMenu(false);
                  onDeepResearchToggle?.(newState);
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
              </svg>
              Deep research
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatInput;
